import React, { useState, useEffect } from "react";

import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";

import { styled } from "@mui/material/styles";
import { Box, Typography, Button, CircularProgress } from "@mui/material";

import CollapseCard from "components/Collapse";
import TwoColumnContainer from "components/Container/TwoColumnContainer";
import ChipTag from "components/ChipTag";
import { formatTimestamp, formatDuration } from "utils/format";

import { ReactComponent as HideIcon } from "assets/images/icons/icn_input_hide.svg";
import { ReactComponent as ShowIcon } from "assets/images/icons/icn_input_show.svg";

const StyledTypography = styled((props) => (
  <Typography component="div" {...props} />
))(({ theme }) => ({
  fontSize: theme.typography.customFontSize[2],
  color: theme.palette.primary.black,
  fontWeight: theme.typography.customFontWeight[1],
}));

const StyledIconButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.transparent,
  width: "16px",
  height: "16px",
  minWidth: "16px",
  padding: "0",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.transparent,
  },
}));

const DevicePanel = ({
  fetchFlag,
  auth,
  standalone,
  infoData,
  capabilityData,
  batchLoading,
  deviceTags,
  setDeviceTags,
  timeZone,
  deviceAccount,
  showSecret,
  setShowSecret,
  fetchTableData,
}) => {
  const isInventoryDevice = !infoData.siteId;
  const supportPoePower =
    !!capabilityData.poePorts && capabilityData.poePorts.length > 0;

  const { dataMap, getData, sendRequest } = useDataSourceManager();

  const [createDeviceTagsUuid, _setCreateDeviceTagsUuid] = useState(uuidv4());
  const [addAndRemovedDeviceTagsUuid, _setAddAndRemovedDeviceTagsUuid] =
    useState(uuidv4());
  const [editDeviceTagsUuid, _setEditDeviceTagsUuid] = useState(uuidv4());
  const [deleteDeviceTagsUuid, _setDeleteDeviceTagsUuid] = useState(uuidv4());

  const [chipsData, setChipsData] = useState([]);
  const [
    temporaryStorageAddAndRemovedDeviceTag,
    setTemporaryStorageAddAndRemovedDeviceTag,
  ] = useState({});
  const [newDeviceTag, setNewDeviceTag] = useState({});

  const fields = [
    { key: "siteName", label: "Site", hidden: false },
    { key: "syncStatus", label: "Sync Status", hidden: false },
    { key: "modelName", label: "Model", hidden: false },
    { key: "hardwareVersion", label: "Hardware Version", hidden: false },
    // 2025/11/28 隱藏
    // { key: "serialNumber", label: "Serial Number" },
    {
      key: "firmwareVersion",
      label: "Firmware Version",
      withUpdate: true,
      hidden: false,
    },
    ...(infoData.deviceType === "router"
      ? [{ key: "connectedClient", label: "Connected Client" }]
      : []),
    ...(isInventoryDevice
      ? []
      : [{ key: "cpuStats", label: "CPU Utilization", hidden: false }]),
    ...(isInventoryDevice
      ? []
      : [{ key: "memoryStats", label: "Memory Utilization", hidden: false }]),

    {
      key: "poePowerUsage",
      label: "PoE Power Usage",
      hidden: !supportPoePower,
    },
    { key: "uptime", label: "Uptime", hidden: false },
    { key: "address", label: "Address", hidden: false },
    ...(isInventoryDevice ? [] : [{ key: "deviceTag", label: "Device Tag" }]),
    ...(capabilityData.tunnelAccess === true //tunnelAccess為true的時候要把timezone欄位隱藏(參考task2112規則)
      ? []
      : [{ key: "timeZone", label: "Time Zone" }]),
  ];

  useEffect(() => {
    setChipsData(infoData.deviceTag);
  }, [infoData]);

  function createDeviceTag(createTagName) {
    const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/settings/tags/deviceTags.create`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites.id,
      },
      nonStandalone: {
        entId: auth.realm.id,
        orgId: auth.orgs.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites.id,
      },
    };

    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    const requestParams = {
      data: {
        tagName: createTagName,
      },
    };

    sendRequest(apiUrl, createDeviceTagsUuid, restfulParams, requestParams);
  }

  function addAndRemovedDeviceTag(updateTag, mode) {
    const baseOrgUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
    const baseSiteUrl = standalone
      ? `/orgs/:orgId`
      : `/ents/:entId/orgs/:orgId/sites/:siteId`;

    const baseRestfulParams = standalone
      ? {
          orgId: auth.realm.id,
          deviceId: infoData.macAddress,
        }
      : {
          entId: auth.realm.id,
          orgId: auth.realm.id,
          deviceId: infoData.macAddress,
        };

    const requestConfig = {
      org: {
        apiKey: `${baseOrgUrl}/devices/:deviceId/settings.update`,
        restfulParams: baseRestfulParams,
      },
      site: {
        apiKey: `${baseSiteUrl}/devices/:deviceId/settings.update`,
        restfulParams: baseRestfulParams,
      },
    };

    const currentConfig = requestConfig[auth.scopeLevel];
    const addTagData = chipsData.map((item) => item.tagId);
    const removeTagData = chipsData.filter((item) => item.tagId !== updateTag);

    const currentDeviceTag =
      mode === "add"
        ? [...addTagData, updateTag]
        : removeTagData.map((item) => item.tagId);

    const requestParams = {
      data: {
        deviceTag: currentDeviceTag,
      },
    };

    sendRequest(
      currentConfig.apiKey,
      addAndRemovedDeviceTagsUuid,
      currentConfig.restfulParams,
      requestParams,
    );
  }

  function editDeviceTag(editTag) {
    const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/settings/tags/deviceTags.update`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites.id,
      },
      nonStandalone: {
        entId: auth.realm.id,
        orgId: auth.orgs.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites.id,
      },
    };

    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    const requestParams = {
      data: {
        ...editTag,
      },
    };

    sendRequest(apiUrl, editDeviceTagsUuid, restfulParams, requestParams);
  }

  function deleteDeviceTag(deleteTag) {
    const apiUrl = `${standalone ? "" : "/ents/:entId"}/orgs/:orgId/sites/:siteId/settings/tags/deviceTags.delete`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites.id,
      },
      nonStandalone: {
        entId: auth.realm.id,
        orgId: auth.orgs.id,
        siteId: auth.scopeLevel === "org" ? infoData.siteId : auth.sites.id,
      },
    };

    const mode = standalone ? "standalone" : "nonStandalone";
    const restfulParams = restfulParamsConfig[mode];

    const requestParams = {
      data: {
        tagIds: [deleteTag.tagId],
      },
    };

    sendRequest(apiUrl, deleteDeviceTagsUuid, restfulParams, requestParams);
  }

  useEffect(() => {
    const createDeviceTagResponse = getData(createDeviceTagsUuid);
    if (createDeviceTagResponse && createDeviceTagResponse.data) {
      const newDeviceTagId = createDeviceTagResponse.data.tagId;
      const newTag = { tagId: newDeviceTagId, tagName: newDeviceTag.tagName };
      setDeviceTags((prev) => [...prev, newTag]);
      setNewDeviceTag({});
    }
    const addAndRemovedDeviceTagResponse = getData(addAndRemovedDeviceTagsUuid);
    if (addAndRemovedDeviceTagResponse && addAndRemovedDeviceTagResponse.data) {
      if (temporaryStorageAddAndRemovedDeviceTag.mode === "add") {
        setChipsData((pre) => [
          ...pre,
          {
            tagId: temporaryStorageAddAndRemovedDeviceTag.tagId,
            tagName: temporaryStorageAddAndRemovedDeviceTag.tagName,
          },
        ]);
        fetchFlag === false && fetchTableData();
      } else {
        const removeTagData = chipsData.filter(
          (item) => item.tagId !== temporaryStorageAddAndRemovedDeviceTag.tagId,
        );
        setChipsData(removeTagData);
        fetchFlag === false && fetchTableData();
      }
    }
    const editDeviceTagResponse = getData(editDeviceTagsUuid);
    if (editDeviceTagResponse && editDeviceTagResponse.data) {
      const newEditDeviceTag = editDeviceTagResponse.data;

      const updatedChips = chipsData.map((chip) =>
        chip.tagId === newEditDeviceTag.tagId ? newEditDeviceTag : chip,
      );
      const updatedTags = deviceTags.map((tag) =>
        tag.tagId === newEditDeviceTag.tagId
          ? { ...tag, tagName: newEditDeviceTag.tagName }
          : tag,
      );
      setChipsData(updatedChips);
      setDeviceTags(updatedTags);
      fetchFlag === false && fetchTableData();
    }
    const deleteDeviceTagResponse = getData(deleteDeviceTagsUuid);
    if (deleteDeviceTagResponse && !("code" in deleteDeviceTagResponse)) {
      const updatedChips = chipsData.filter(
        (item) => item.tagId !== newDeviceTag.tagId,
      );
      const updatedTags = deviceTags.filter(
        (item) => item.tagId !== newDeviceTag.tagId,
      );
      setChipsData(updatedChips);
      setDeviceTags(updatedTags);
      setNewDeviceTag({});
      fetchFlag === false && fetchTableData();
    }
  }, [dataMap]);

  return (
    <CollapseCard title="Device">
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {fields.map(({ key, label, withUpdate, hidden }) => {
          if (hidden) return null;

          if (key === "uptime" && infoData.linkStatus === "offline") {
            key = "lastSeen";
            label = "Last Seen";
          }

          let displayValue;

          switch (key) {
            case "deviceTag":
              displayValue = (batchLoading && <CircularProgress />) || (
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  {(!Array.isArray(chipsData) || chipsData.length === 0) && (
                    <Box sx={{ marginRight: 1 }}>-</Box>
                  )}
                  <ChipTag
                    chips={chipsData}
                    tags={deviceTags}
                    onChipsRemoveChange={(props) => {
                      const { removedChip } = props;
                      setTemporaryStorageAddAndRemovedDeviceTag({
                        ...removedChip,
                        mode: "remove",
                      });
                      addAndRemovedDeviceTag(removedChip.tagId, "remove");
                    }}
                    onChipsAddChange={(props) => {
                      const { newChip } = props;
                      setTemporaryStorageAddAndRemovedDeviceTag({
                        ...newChip,
                        mode: "add",
                      });
                      addAndRemovedDeviceTag(newChip.tagId, "add");
                    }}
                    onTagsChange={(props) => {
                      const { newTag } = props;
                      setNewDeviceTag(newTag);
                      createDeviceTag(newTag.tagName);
                    }}
                    onChipsEditChange={(props) => {
                      const { editedChip } = props;
                      editDeviceTag(editedChip);
                    }}
                    onModalDeleteClick={(props) => {
                      const { deleteChip } = props;
                      setNewDeviceTag(deleteChip);
                      deleteDeviceTag(deleteChip);
                    }}
                  />
                </Box>
              );
              break;
            case "poePowerUsage": {
              const { poeConsumption, poeBudget } = infoData;
              displayValue =
                poeConsumption != null && poeBudget != null
                  ? `${poeConsumption.toFixed(1)}/${poeBudget}W`
                  : "-";
              break;
            }
            case "cpuStats":
            case "memoryStats":
              displayValue =
                infoData[key] != null
                  ? `${Number(infoData[key]).toFixed(2)}%`
                  : "-";
              break;
            case "lastSeen":
              displayValue =
                infoData.linkStatus === "online"
                  ? "online"
                  : infoData.lastOfflineTime
                    ? formatTimestamp(infoData.lastOfflineTime)
                    : "-";
              break;
            case "uptime": {
              const status = infoData.linkStatus;
              const uptime = infoData.uptime;
              const lastSeen = infoData.lastOfflineTime;

              if (status === "offline") {
                displayValue =
                  lastSeen !== null ? formatTimestamp(lastSeen) : "-";
              } else if (status === "online") {
                displayValue =
                  uptime !== null
                    ? formatDuration(
                        Math.max(0, Math.floor(Date.now() / 1000) - uptime),
                      ) // 算法參考#1094
                    : "-";
              } else {
                displayValue = "-";
              }

              break;
            }
            case "timeZone":
              displayValue = (batchLoading && <CircularProgress />) || timeZone;
              break;
            default:
              displayValue = infoData[key] != null ? infoData[key] : "-";
          }

          return (
            <TwoColumnContainer key={key} label={label}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <StyledTypography>{displayValue}</StyledTypography>
                {/* 隱藏功能不開放 */}
                {/* {withUpdate &&
                  (auth.permissions === "Super Admin" ||
                    auth.permissions === "Admin") && (
                    <SolidButton width="100px" height="32px">
                      Update
                    </SolidButton>
                  )} */}
              </Box>
            </TwoColumnContainer>
          );
        })}

        <TwoColumnContainer label="Device Account">
          {(batchLoading && <CircularProgress />) || (
            <StyledTypography>
              Username: {deviceAccount.userName}
            </StyledTypography>
          )}
        </TwoColumnContainer>
        {!batchLoading && (
          <TwoColumnContainer label="">
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "center",
              }}
            >
              <StyledTypography>
                Password:{" "}
                {showSecret
                  ? deviceAccount.secret || "-"
                  : deviceAccount.secret
                    ? "••••••••••"
                    : "-"}
              </StyledTypography>
              <StyledIconButton onClick={() => setShowSecret((prev) => !prev)}>
                {showSecret ? <HideIcon /> : <ShowIcon />}
              </StyledIconButton>
            </Box>
          </TwoColumnContainer>
        )}
        <TwoColumnContainer label="Joined On">
          <StyledTypography>
            {infoData?.joinedOn ? formatTimestamp(infoData.joinedOn) : "-"}
          </StyledTypography>
        </TwoColumnContainer>
      </Box>
    </CollapseCard>
  );
};

export default DevicePanel;
